import { FAST_API_URL, postOptions } from "../index";

// ── Types ──────────────────────────────────────────────

export interface PostProcessProposal {
    subtitle_id: string;
    old_text: string;
    new_text: string;
    alt_text?: string;
    confidence: "high" | "medium";
}

export interface ScanResponse {
    proposals: PostProcessProposal[];
    total_scanned: number;
    total_changed: number;
}

export interface ApplyResponse {
    applied: number;
    failed: number;
}

export type ScanEvent =
    | { type: "progress"; current: number; total: number }
    | { type: "proposal"; subtitle_id: string; old_text: string; new_text: string; alt_text?: string; confidence: "high" | "medium" }
    | { type: "done"; total_scanned: number; total_changed: number }
    | { type: "error"; detail: string };

// ── API Functions ──────────────────────────────────────

/**
 * Streaming scan: reads NDJSON events from the server one by one.
 * Calls onEvent for each event as it arrives.
 */
export const translationPostProcessScanStream = async (
    params: {
        mediaId: string;
        folderNum: number;
        lang: string;
        languageFrom: string;
        step: string;
    },
    onEvent: (event: ScanEvent) => void,
): Promise<void> => {
    const response = await fetch(
        `${FAST_API_URL}/translation_post_process_scan?media=${params.mediaId}`,
        {
            ...postOptions,
            body: JSON.stringify({
                folder_num: params.folderNum,
                lang: params.lang,
                language_from: params.languageFrom,
                step: params.step,
            }),
        }
    );

    if (!response.ok) {
        throw new Error("Scan failed");
    }

    const reader = response.body?.getReader();
    if (!reader) throw new Error("No response body");

    const decoder = new TextDecoder();
    let buffer = "";

    while (true) {
        const { done, value } = await reader.read();
        if (done) break;

        buffer += decoder.decode(value, { stream: true });
        const lines = buffer.split("\n");
        buffer = lines.pop() || ""; // keep incomplete line in buffer

        for (const line of lines) {
            const trimmed = line.trim();
            if (!trimmed) continue;
            try {
                const event = JSON.parse(trimmed) as ScanEvent;
                onEvent(event);
            } catch {
                // skip unparseable lines
            }
        }
    }

    // Process any remaining buffer
    if (buffer.trim()) {
        try {
            const event = JSON.parse(buffer.trim()) as ScanEvent;
            onEvent(event);
        } catch {
            // ignore
        }
    }
};

export const translationPostProcessApplyApi = async (params: {
    mediaId: string;
    folderNum: number;
    lang: string;
    languageFrom: string;
    step: string;
    changes: { subtitle_id: string; new_text: string }[];
}): Promise<ApplyResponse> => {
    const response = await fetch(
        `${FAST_API_URL}/translation_post_process_apply?media=${params.mediaId}`,
        {
            ...postOptions,
            body: JSON.stringify({
                folder_num: params.folderNum,
                lang: params.lang,
                language_from: params.languageFrom,
                step: params.step,
                changes: params.changes,
            }),
        }
    );

    if (!response.ok) {
        throw new Error("Apply failed");
    }

    return await response.json();
};
